import { microServicesRoutes } from '@constants';
import { type ApiResponse, type ScriptSketchProps, type ScriptType } from '@types';
import { contactsApi } from '@lib/apiClients';
import { logError } from '@lib/logger/logError';
import { extractErrorMessage } from '@lib/logger/extractErrorMessage';

export default function useIntentApi (): {
  getScriptsByOrgId: (orgId: string) => Promise<ApiResponse<ScriptType[]>>;
  createScript: (
    script: Omit<ScriptType, 'id'>,
  ) => Promise<ApiResponse<ScriptType>>;
  updateScript: (
    id: string,
    script: Partial<Omit<ScriptType, 'id' | 'organizationId'>>,
  ) => Promise<ApiResponse<ScriptType>>;
  deleteScript: (id: string) => Promise<ApiResponse<null>>;
  sketchWithAI: (userId:string, prompt: string) => Promise<ApiResponse<ScriptSketchProps|null>>;
  } {
  const handleError = <T = never>(
    message: string,
    error: unknown,
    source: string,
  ): ApiResponse<T> => {
    const errorMessage = extractErrorMessage(error);

    void logError(
      message,
      source,
      {
        error: errorMessage,
        rawError: error,
      },
      false,
    );

    return {
      success: false,
      error: {
        message,
        raw: errorMessage,
      },
    };
  };

  const getScriptsByOrgId = async function (
    orgId: string,
  ): Promise<ApiResponse<ScriptType[]>> {
    try {
      const url = `${microServicesRoutes.contacts.get.intent.scriptListNestByOrgId}/${orgId}`;
      const response = await contactsApi.get<ScriptType[]>(url);
      return {
        success: true,
        data: response.data,
      };
    } catch (e) {
      return handleError(
        'Error fetching scripts by orgId.',
        e,
        'getScriptsByOrgId',
      );
    }
  };

  const createScript = async function (
    script: Omit<ScriptType, 'id'>,
  ): Promise<ApiResponse<ScriptType>> {
    try {
      const url = microServicesRoutes.contacts.post.intent.scriptNest;
      const response = await contactsApi.post<ScriptType>(url, script);
      return {
        success: true,
        data: response.data,
      };
    } catch (e) {
      return handleError('Error creating script.', e, 'createScript');
    }
  };

  const updateScript = async function (
    id: string,
    script: Partial<Omit<ScriptType, 'id' | 'organizationId'>>,
  ): Promise<ApiResponse<ScriptType>> {
    try {
      const url = `${microServicesRoutes.contacts.put.intent.editScriptNest}/${id}`;
      const response = await contactsApi.put<ScriptType>(url, script);
      return {
        success: true,
        data: response.data,
      };
    } catch (e) {
      return handleError('Error updating script.', e, 'updateScript');
    }
  };

  const deleteScript = async function (id: string): Promise<ApiResponse<null>> {
    try {
      const url = `${microServicesRoutes.contacts.delete.intent.deleteScriptNest}/${id}`;
      await contactsApi.delete(url);
      return { success: true };
    } catch (e) {
      return handleError('Error deleting script.', e, 'deleteScript');
    }
  };
  
  const sketchWithAI = async function (userId:string, prompt: string): Promise<ApiResponse<ScriptSketchProps|null>> {
    try {
      const url = `${microServicesRoutes.contacts.post.intent.generateAIScript}`;
      const promptBody = { userId, prompt };
      const response = await contactsApi.post<ScriptSketchProps>(url, promptBody);
      return {
        success: true,
        data: response.data,
      };
    } catch (e) {
      return handleError('Error generating script with AI.', e, 'sketchWithAI');
    }
  };

  return {
    getScriptsByOrgId,
    createScript,
    updateScript,
    deleteScript,
    sketchWithAI
  };
}
